import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/PrizeReceivedNotification.css';
import { PrizeNotification } from '../types/prizeNotification';
import { markNotificationsAsRead } from '../services/prizeShareService';

interface PrizeReceivedNotificationProps {
  notifications: { [senderId: string]: PrizeNotification[] };
  onClose: () => void;
}

const PrizeReceivedNotification: React.FC<PrizeReceivedNotificationProps> = ({
  notifications,
  onClose
}) => {
  const navigate = useNavigate();
  const [currentSenderIndex, setCurrentSenderIndex] = useState(0);

  const senderIds = Object.keys(notifications);
  
  if (senderIds.length === 0) {
    return null;
  }

  const currentSenderId = senderIds[currentSenderIndex];
  const currentNotifications = notifications[currentSenderId];
  const firstNotification = currentNotifications[0];
  const prizeCount = currentNotifications.length;

  const handleNext = async () => {
    // Mark current notifications as read
    const notificationIds = currentNotifications.map(n => n.id);
    try {
      await markNotificationsAsRead(notificationIds);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }

    // Move to next sender or close
    if (currentSenderIndex < senderIds.length - 1) {
      setCurrentSenderIndex(currentSenderIndex + 1);
    } else {
      onClose();
    }
  };

  const handleViewWallet = async () => {
    // Mark all notifications as read
    const allNotificationIds = senderIds.flatMap(senderId => 
      notifications[senderId].map(n => n.id)
    );
    
    try {
      await markNotificationsAsRead(allNotificationIds);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }

    onClose();
    navigate('/wallet');
  };

  return (
    <div className="prize-received-notification-overlay">
      <div className="prize-received-notification-modal">
        <div className="prize-received-notification-content">
          {/* Brand Logo */}
          <div className="prize-received-logo-container">
            <img 
              src={firstNotification.brandLogo}
              alt="Brand"
              className="prize-received-brand-logo"
            />
          </div>

          {/* Message */}
          <div className="prize-received-message">
            {prizeCount === 1 ? (
              <>
                <h2>Du har fået en præmie!</h2>
                <p className="prize-received-from">
                  fra <strong>{firstNotification.senderUsername}</strong>
                </p>
                <p className="prize-received-prize-name">
                  {firstNotification.prizeName}
                </p>
              </>
            ) : (
              <>
                <h2>Du har fået {prizeCount} præmier!</h2>
                <p className="prize-received-from">
                  fra <strong>{firstNotification.senderUsername}</strong>
                </p>
                <div className="prize-received-list">
                  {currentNotifications.map((notification, index) => (
                    <p key={index} className="prize-received-prize-name-small">
                      • {notification.prizeName}
                    </p>
                  ))}
                </div>
              </>
            )}

            {/* Gift Message if present */}
            {firstNotification.giftMessage && (
              <div className="prize-received-gift-message">
                <p className="gift-message-label">Besked:</p>
                <p className="gift-message-text">"{firstNotification.giftMessage}"</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="prize-received-actions">
            <button
              onClick={handleViewWallet}
              className="prize-received-wallet-btn"
            >
              Se Wallet
            </button>
            
            {senderIds.length > 1 && currentSenderIndex < senderIds.length - 1 && (
              <button
                onClick={handleNext}
                className="prize-received-next-btn"
              >
                Næste ({currentSenderIndex + 1}/{senderIds.length})
              </button>
            )}
            
            {(senderIds.length === 1 || currentSenderIndex === senderIds.length - 1) && (
              <button
                onClick={handleNext}
                className="prize-received-close-btn"
              >
                Luk
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrizeReceivedNotification;
